import { Expedition, generateExpedition, EXPEDITIONS } from './expedition.js';
import { SWORD_POWER } from './crafting.js';
// A playable level: builds the world from the generator, runs entities, scoring and flow.
import * as THREE from 'three';
import { VoxelWorld } from './voxel.js';
import { BLOCKS } from './blocks.js';
import { THEMES, WORLDS, parseLevelId, levelName } from './themes.js';
import { generateCourse, generateArena } from './levelgen.js';
import { Player } from './player.js';
import { CoinField, Loot, Pickup, MovingPlatform, CrumbleBlock, Checkpoint, BrainGate, LuckyBlock, Spinner, Portal, Chest, NPC, Sign } from './entities.js';
import { Mob } from './mobs.js';
import { Boss } from './boss.js';
import { Pet } from './pet.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { catalogItem } from './progression.js';

const _v = new THREE.Vector3();

export class Level {
  constructor(game, id) {
    this.game = game;
    this.id = id;
    const { w, l } = parseLevelId(id);
    this.wi = w;
    this.li = l;
    this.W = WORLDS[w - 1];
    this.themeKey = this.W.theme;
    this.theme = THEMES[this.themeKey];
    this.isBoss = l === 4;
    this.name = levelName(id);
    this.group = new THREE.Group();
    game.scene.add(this.group);
    this.mats = game.mats;
    this.world = new VoxelWorld(game.mats);
    this.group.add(this.world.group);
    this.colliders = [];
    this.hittables = [];
    this.entities = [];
    this.mobs = [];
    this.fx = game.fx;
    this.time = 0;
    this.state = 'build';
    this.stats = { coins: 0, gems: 0, asked: 0, firstTry: 0, mobs: 0, stomps: 0, secret: false, falls: 0, jumps: 0, xp: 0, lucky: 0, chests: 0, bounces: 0, gates: 0 };
    this.offer = null;
    this.lastOffer = null;
    this.coinChain = 0;
    this.lastCoinT = -9;
    this.worldDirty = false;
  }

  get save() {
    return this.game.save.data;
  }

  build() {
    const g = this.game;
    const seed = this.wi * 1000 + this.li * 37 + 11;
    const difficulty = (this.wi - 1) * 3 + (this.li - 1);
    let spec;
    if(EXPEDITIONS[this.id.split('-')[1]])spec=generateExpedition(this.world,this.id);
    else if (this.isBoss) spec = generateArena(this.world, this.theme, this.W.boss.kind);
    else
      spec = generateCourse(this.world, this.theme, {
        seed,
        difficulty: Math.min(14, difficulty),
        tutorial: this.id === '1-1',
        quietGuides: this.id !== '1-1' || !!this.save.levels['1-1']?.done,
        introText: this.id === '1-3' && this.save.unlocks.doubleJump
          ? 'New move, {name}! Jump again in the air for a <b>double jump</b>. Follow the coins to the portal!'
          : undefined,
        doubleJump: this.save.unlocks.doubleJump,
        levelName: this.name,
      });
    this.spec = spec;
    this.killY = spec.killY;
    // entities
    const coins = [];
    let npcId = 0;
    for (const e of spec.ents) {
      if (['npc', 'lucky', 'brainblock', 'chest', 'sign'].includes(e.type) || (e.type === 'mob' && e.kind !== 'flyer')) this.ensureGround(e);
      switch (e.type) {
        case 'coin':
          coins.push(e);
          break;
        case 'gem':
          this.entities.push(new Pickup(this, 'gem', e.x, e.y, e.z));
          break;
        case 'star':
          this.entities.push(new Pickup(this, 'star', e.x, e.y, e.z));
          break;
        case 'heart':
          this.entities.push(new Pickup(this, 'heart', e.x, e.y, e.z));
          break;
        case 'powerup':
          this.entities.push(new Pickup(this, 'powerup', e.x, e.y, e.z, { power: e.kind }));
          break;
        case 'moving':
          this.entities.unshift(new MovingPlatform(this, e));
          break;
        case 'crumble':
          this.entities.push(new CrumbleBlock(this, e));
          break;
        case 'checkpoint': {
          const cp = new Checkpoint(this, e);
          this.entities.push(cp);
          if (e.start) this.startCP = cp;
          break;
        }
        case 'gate':
          this.entities.push(new BrainGate(this, e));
          break;
        case 'lucky':
          this.entities.push(new LuckyBlock(this, e.x, e.y, e.z, false));
          break;
        case 'brainblock':
          this.entities.push(new LuckyBlock(this, e.x, e.y, e.z, true));
          break;
        case 'spinner':
          this.entities.push(new Spinner(this, e));
          break;
        case 'portal':
          this.portal = new Portal(this, { ...e, c1: this.themeKey === 'lava' ? '#ff4a1a' : undefined, c2: this.themeKey === 'lava' ? '#ffd040' : undefined });
          this.entities.push(this.portal);
          break;
        case 'chest':
          this.entities.push(new Chest(this, e));
          break;
        case 'npc':
          this.entities.push(new NPC(this, e, npcId++));
          break;
        case 'sign':
          this.entities.push(new Sign(this, e));
          break;
        case 'mob':
          this.addMob(e.kind, e.x, e.y, e.z);
          break;
      }
    }
    this.coins = new CoinField(this, coins);
    this.totalCoins = coins.length;
    this.world.rebuild();
    // player
    this.character = g.makePlayerCharacter();
    this.group.add(this.character.root);
    this.character.setArmour(this.save.discovery.expansion?.gear);
    this.player = new Player(this, this.character);
    const petDef = catalogItem('pet', this.save.equip.pet);
    this.player.maxHearts = 3 + (petDef?.heart || 0);
    this.player.hearts = this.player.maxHearts;
    const sp = spec.spawn;
    this.player.spawn(new THREE.Vector3(sp.x, sp.y, sp.z), sp.yaw);
    this.player.invuln = 0; // no blinking at the very start
    this.player.lastSafe.copy(this.player.pos);
    if (this.startCP) this.startCP.activate(true);
    this.checkpoint = this.startCP || { pos: this.player.pos.clone() };
    if (this.save.equip.pet && this.save.equip.pet !== 'none') {
      this.pet = new Pet(this, this.save.equip.pet);
      this.entities.push(this.pet);
    }
    if (this.isBoss) {
      this.boss = new Boss(this, this.W.boss, this.theme);
      this.portal = new Portal(this, { x: 0.5, y: 41, z: 12.5, active: false });
      this.entities.push(this.portal);
    }
    const startZ = sp.z, endZ = spec.endZ ?? sp.z + 10;
    this.progressRange = [startZ, endZ];
    g.camRig.snap(this.player.pos, sp.yaw);
    if(spec.expedition)this.expedition=new Expedition(this);
    this.state = 'intro';
  }

  addMob(kind, x, y, z) {
    const m = new Mob(this, kind, x, y, z, { slimeColor: this.theme.slimeColor, flyerKind: this.themeKey === 'glitch' ? 'glitch' : this.themeKey === 'snow' ? 'ice' : 'fire' });
    this.mobs.push(m);
    return m;
  }

  ensureGround(e) {
    const x = Math.floor(e.x), z = Math.floor(e.z);
    const y = Math.floor(e.y - (e.type === 'lucky' || e.type === 'brainblock' ? 0.5 : 0));
    const below = this.world.get(x, y - 1, z);
    if (below && BLOCKS[below].solid) return;
    for (let k = 1; k <= 3; k++) {
      const id = this.world.get(x, y - 1 - k, z);
      if (id && BLOCKS[id].solid) {
        for (let j = 1; j <= k; j++) this.world.set(x, y - j, z, j === 1 ? this.theme.surface : this.theme.sub);
        return;
      }
    }
    this.world.set(x, y - 1, z, this.theme.surface);
    this.world.set(x, y - 2, z, this.theme.sub);
  }

  // ---------------- flow ----------------
  async intro() {
    const g = this.game;
    const path = this.spec.path;
    g.ui.titleCard(this);
    if (this.isBoss) {
      await g.camRig.playPath([{ x: 0, y: 70, z: -60 }, { x: 30, y: 60, z: -10 }, { x: 10, y: 50, z: 25 }, { x: 0, y: 46, z: -20 }], 4.2, 0.1);
      if (this.disposed) return;
      if (this.boss) this.boss.roar();
    } else {
      const pts = [];
      const last = path[path.length - 1];
      pts.push({ x: last.x + 18, y: last.y + 18, z: last.z + 16 });
      for (let i = path.length - 1; i >= 0; i -= Math.max(1, Math.floor(path.length / 6))) pts.push({ x: path[i].x + 9, y: path[i].y + 12, z: path[i].z + 4 });
      const s = this.player.pos;
      pts.push({ x: s.x + 2, y: s.y + 6, z: s.z - 9 });
      await g.camRig.playPath(pts, Math.min(6, 2.5 + path.length * 0.28), 0.06);
    }
    if(this.disposed)return;
    g.camRig.snap(this.player.pos, this.spec.spawn.yaw);
    g.ui.hideTitleCard();
    this.state = 'play';
    g.ui.go();
    audio.play('go');
  }

  skipIntro() {
    this.game.camRig.skipCine();
  }

  update(dt, input) {
    if (this.state === 'build') return;
    this.time += dt;
    const g = this.game;
    if (this.state === 'intro') {
      if (input.jumpPressed || input.actionPressed || input.taps.length || input.pausePressed) this.skipIntro();
      this.updateEntities(dt, true);
      this.player.ch.update(dt, { speed: 0, grounded: true });
      g.camRig.update(dt, NO_INPUT, this.player.pos, this.world);
      return;
    }
    if (this.state === 'play') this.stats.time = (this.stats.time || 0) + dt;
    // interaction / sword: hitting something in front of Sai always wins over a prompt
    if (this.state === 'play' && input.actionPressed) {
      if (this.lastOffer && !this.facingHittable()) {
        const o = this.lastOffer;
        this.lastOffer = null;
        audio.play('click');
        o.cb();
      } else this.player.swing();
    }
    this.offer = null;
    if (this.state === 'quiz') {
      // keep the world alive behind the quiz, but freeze gameplay
      for (const e of this.entities) if (e instanceof BrainGate || e instanceof Pickup || e instanceof Checkpoint) e.update(dt, this.time);
      this.cleanup();
      this.player.ch.update(dt, { speed: 0, grounded: true });
      if (this.worldDirty) {
        this.world.rebuild();
        this.worldDirty = false;
      }
      g.camRig.update(dt, { camDX: 0, camDY: 0, wheel: 0 }, this.player.pos, this.world);
      return;
    }
    // moving platforms first so the player can ride them
    for (const e of this.entities) if (e instanceof MovingPlatform) e.update(dt);
    const camYaw = g.camRig.yaw;
    if(!this.expedition?.drive(dt,input))this.player.update(dt, this.state === 'play' ? input : NO_INPUT, camYaw);
    if(this.save.discovery.expansion?.gear.shield&&input.wasPressed?.('f'))this.player.power.shield=2;
    this.updateEntities(dt, false);
    if (this.worldDirty) {
      this.world.rebuild();
      this.worldDirty = false;
    }
    // camera
    if (this.state === 'win') this.winCam(dt);
    else
      g.camRig.update(dt, this.state === 'play' ? input : NO_INPUT, this.player.pos, this.world, {
        followYaw: this.isBoss ? undefined : 0,
        moving: Math.hypot(this.player.vel.x, this.player.vel.z) > 2 && input.moveY > 0.5,
      });
    // prompt
    this.expedition?.update();
    this.lastOffer = this.offer;
    if (this.offer && !this.facingHittable() && this.state === 'play') {
      this.fx.worldUI.anchor('prompt', `${this.offer.text}<span class="pkey">${g.input.touchMode ? 'Tap ' + this.offer.icon : 'Press E'}</span>`, this.offer.pos, 'prompt');
      g.input.setActionLabel(this.offer.label, this.offer.icon);
    } else {
      this.fx.worldUI.hide('prompt');
      g.input.setActionLabel('HIT', '⚔️');
    }
    g.ui.hud.update(this);
  }

  updateEntities(dt, introOnly) {
    const t = this.time;
    // A child should only have one guide asking for attention at a time.
    this.guideNPC = null;
    let nearest = 7;
    if (this.state === 'play') for (const e of this.entities) if (e instanceof NPC) {
      const p = this.player.pos, o = e.ch.root.position;
      const distance = Math.hypot(p.x - o.x, p.z - o.z);
      if (distance < nearest && Math.abs(p.y - o.y) < 4) { this.guideNPC = e; nearest = distance; }
    }
    for (const e of this.entities) if (!(e instanceof MovingPlatform) || introOnly) e.update(dt, t);
    if (!introOnly) {
      for (const m of this.mobs) m.update(dt);
      if (this.boss) this.boss.update(dt);
    }
    this.coins.update(dt, t);
    this.cleanup();
  }

  cleanup() {
    for (let i = this.entities.length - 1; i >= 0; i--)
      if (this.entities[i].dead) {
        this.entities[i].dispose();
        this.entities.splice(i, 1);
      }
    for (let i = this.mobs.length - 1; i >= 0; i--)
      if (this.mobs[i].dead) {
        this.mobs[i].dispose();
        this.mobs.splice(i, 1);
      }
  }

  offerAction(ent, text, label, icon, pos, cb) {
    const p = this.player.pos;
    const d = _v.copy(pos).sub(p).lengthSq();
    if (!this.offer || d < this.offer.d) this.offer = { ent, text, label, icon, pos: pos.clone(), cb, d };
  }

  // ---------------- events ----------------
  magnetRadius() {
    if (this.player.power.magnet > 0) return 7;
    const pet = catalogItem('pet', this.save.equip.pet);
    return pet?.magnet || 0;
  }

  onCoin(pos, n) {
    const pet = catalogItem('pet', this.save.equip.pet);
    let v = n;
    if (pet?.coinBoost && Math.random() < pet.coinBoost) v += 1;
    this.stats.coins += v;
    if (this.time - this.lastCoinT < 0.7) this.coinChain = Math.min(12, this.coinChain + 1);
    else this.coinChain = 0;
    this.lastCoinT = this.time;
    audio.play('coin', { chain: this.coinChain });
    haptics.play('coin');
    this.fx.particles.sparkle(pos, '#ffe680', 3);
    this.game.ui.hud.coinFly(pos);
  }

  onGem(pos, n) {
    this.stats.gems += n;
    audio.play('gem');
    haptics.play('gem');
    this.fx.particles.burst(pos, { count: 10, colors: ['#7ff8ff', '#ffffff'], speed: 3, up: 3, size: 0.09, glow: true, bright: 2 });
    this.fx.worldUI.float(`+${n} 💎`, pos, 'ft-cyan');
    this.game.ui.hud.bump('gems');
  }

  onPickup(kind, pos, extra) {
    const P = this.fx.particles;
    if (kind === 'gem') this.onGem(pos, 1);
    else if (kind === 'star') {
      this.stats.secret = true;
      audio.play('star');
      haptics.play('star');
      P.confetti(pos, 70, 1);
      P.burst(pos, { count: 30, colors: ['#ffd23f', '#ffffff'], speed: 6, up: 6, glow: true, bright: 2.2, size: 0.12 });
      this.fx.rings.add(pos.clone().setY(pos.y - 2), { color: '#ffd23f', to: 5 });
      this.fx.worldUI.float('⭐ SECRET STAR! ⭐', pos, 'ft-gold ft-huge', { life: 2.2, rise: 90 });
      this.game.ui.hud.starFound();
      this.addXP(40);
    } else if (kind === 'heart') {
      this.player.heal(1);
      audio.play('heart');
      haptics.play('heart');
      P.burst(pos, { count: 12, colors: ['#ff4d6d', '#ffffff'], speed: 3, up: 3, glow: true, size: 0.1 });
      this.fx.worldUI.float('+1 ❤️', pos, 'ft-red');
    } else if (kind === 'powerup') {
      const pw = extra.power;
      const dur = { magnet: 15, speed: 10, jump: 12, shield: 10 }[pw] || 10;
      this.player.power[pw] = dur;
      audio.play('powerup');
      haptics.play('powerup');
      P.burst(pos, { count: 20, colors: ['#ffffff', '#ffd23f', '#7be35a'], speed: 5, up: 4, glow: true, size: 0.1 });
      const names = { magnet: '🧲 COIN MAGNET!', speed: '⚡ SPEED BOOTS!', jump: '🦘 SUPER JUMP!', shield: '🛡️ SHIELD!' };
      this.fx.worldUI.float(names[pw], pos, 'ft-gold ft-big', { life: 1.6 });
    }
  }

  spawnLoot(pos, kind) {
    this.entities.push(new Loot(this, pos.clone(), kind));
  }

  rollLucky(pos) {
    this.stats.lucky++;
    const r = Math.random();
    const P = this.fx.particles;
    audio.play('lucky');
    haptics.play('lucky');
    if (r < 0.42) {
      const n = 8 + Math.floor(Math.random() * 8);
      for (let i = 0; i < n; i++) this.spawnLoot(pos, 'coin');
      this.fx.worldUI.float(`🪙 COIN SHOWER!`, pos.clone().add(new THREE.Vector3(0, 1, 0)), 'ft-gold ft-big');
    } else if (r < 0.62) {
      for (let i = 0; i < 3; i++) this.spawnLoot(pos, 'gem');
      this.fx.worldUI.float(`💎 GEMS!`, pos.clone().add(new THREE.Vector3(0, 1, 0)), 'ft-cyan ft-big');
    } else if (r < 0.8) {
      const pw = ['magnet', 'speed', 'jump', 'shield'][Math.floor(Math.random() * 4)];
      this.entities.push(new Pickup(this, 'powerup', pos.x, pos.y + 0.6, pos.z, { power: pw }));
    } else if (r < 0.9 && this.player.hearts < this.player.maxHearts) {
      this.entities.push(new Pickup(this, 'heart', pos.x, pos.y + 0.6, pos.z));
    } else {
      for (let i = 0; i < 20; i++) this.spawnLoot(pos, 'coin');
      for (let i = 0; i < 2; i++) this.spawnLoot(pos, 'gem');
      P.confetti(pos, 50, 1);
      this.fx.worldUI.float('🎰 JACKPOT!!', pos.clone().add(new THREE.Vector3(0, 1, 0)), 'ft-gold ft-huge', { life: 1.8 });
    }
  }

  onChest(pos) {
    this.stats.chests++;
    haptics.play('chest');
    for (let i = 0; i < 15; i++) this.spawnLoot(pos.clone().add(new THREE.Vector3(0, 0.5, 0)), 'coin');
    this.spawnLoot(pos.clone().add(new THREE.Vector3(0, 0.5, 0)), 'gem');
    this.fx.particles.confetti(pos, 40, 0.8);
  }

  onMobDefeated(mob, stomped) {
    this.stats.mobs++;
    if (stomped) this.stats.stomps++;
    haptics.play('mobDie');
    this.addXP(5);
  }

  onGateSolved() {
    this.stats.gates++;
    haptics.play('gate');
    this.addXP(15);
  }

  onPlayerHurt() {
    this.game.ui.flash('rgba(255,40,60,0.35)');
    this.shake(0.5);
  }

  addXP(n) {
    this.stats.xp += n;
  }

  setCheckpoint(cp) {
    this.checkpoint = cp;
  }

  shake(a) {
    this.game.camRig.shake(a);
  }

  // is there a mob or hittable block within sword reach in front of Sai?
  facingHittable() {
    const pl = this.player;
    const fx = Math.sin(pl.facing), fz = Math.cos(pl.facing);
    const check = (p, extra) => {
      const dx = p.x - pl.pos.x, dz = p.z - pl.pos.z, dy = p.y - (pl.pos.y + 0.9);
      const d = Math.hypot(dx, dz);
      if (d > 2.3 + extra || Math.abs(dy) > 1.9) return false;
      return d < 0.6 || (dx * fx + dz * fz) / d > 0.35;
    };
    for (const h of this.hittables) if (!h.dead && check(h.center, 0.5)) return true;
    for (const m of this.mobs) if (!m.dying && check(m.center, m.body.hw)) return true;
    return false;
  }

  swordHit(player) {
    const fx = Math.sin(player.facing), fz = Math.cos(player.facing);
    const origin = player.pos;
    let any = false;
    const reach = 2.3;
    const test = (p, extra = 0) => {
      const dx = p.x - origin.x, dz = p.z - origin.z, dy = p.y - (origin.y + 0.9);
      const d = Math.hypot(dx, dz);
      if (d > reach + extra || Math.abs(dy) > 1.9 + extra * 0.5) return false;
      if (d < 0.6) return true;
      return (dx * fx + dz * fz) / d > 0.15;
    };
    for (const m of this.mobs) {
      if (m.dying) continue;
      if (test(m.center, m.body.hw)) {
        if(this.save.discovery.expansion?.enchant.frost)m.stun=3;
        m.hit(new THREE.Vector3(fx, 0, fz), SWORD_POWER[this.game.save.data.equip.sword] || 1);
        any = true;
      }
    }
    for (const h of this.hittables) {
      if (h.dead) continue;
      if (test(h.center, 0.5)) {
        h.hit(new THREE.Vector3(fx, 0, fz));
        any = true;
        break;
      }
    }
    if (this.boss && this.boss.trySwordHit(origin, fx, fz)) any = true;
    if (any) this.shake(0.25);
    if (any) haptics.play('hit');
  }

  async startQuiz(opts) {
    const g = this.game;
    this.state = 'quiz';
    this.player.frozen = true;
    this.player.vel.set(0, this.player.vel.y, 0);
    g.input.showTouchControls(false);
    this.fx.worldUI.hide('prompt');
    const res = await g.quiz.run({ ...opts, weights: this.W.topics, tables: this.W.tables, boost: this.wi >= 4 ? 0.3 : 0 });
    this.stats.asked += res.asked;
    this.stats.firstTry += res.firstTry;
    this.addXP(res.xp);
    this.stats.coins += res.firstTry * 5;
    if (res.topicUps.length) g.ui.toast(`🧠 Your ${res.topicUps.map((t) => g.topicName(t)).join(' & ')} brain levelled up!`, 'purple');
    g.save.persist();
    g.checkBadges();
    if (this.state === 'quiz') {
      this.state = 'play';
      this.player.frozen = false;
      g.input.showTouchControls(true);
      g.input.reset();
      this.brainBlast();
    }
    return res;
  }

  // after a quiz: push nearby monsters back and daze them so Sai isn't ambushed
  brainBlast() {
    const p = this.player.pos;
    this.player.invuln = Math.max(this.player.invuln, 1.5);
    let any = false;
    for (const m of this.mobs) {
      const d = m.body.pos.distanceTo(p);
      if (d > 7 || m.dying) continue;
      any = true;
      const dir = new THREE.Vector3().subVectors(m.body.pos, p).setY(0);
      if (dir.lengthSq() < 0.01) dir.set(1, 0, 0);
      dir.normalize();
      m.stun = 2.2;
      if (m.kind === 'boom') m.fuse = -1;
      if (!m.flyer) {
        m.body.vel.set(dir.x * 6, 6, dir.z * 6);
        // don't fling it off the island if it's standing near an edge
        if (!m.groundAhead(dir.x * 2.5, dir.z * 2.5)) m.body.vel.set(0, 6, 0);
      } else m.body.vel.set(dir.x * 5, 3, dir.z * 5);
    }
    if (any) {
      this.fx.rings.add(p.clone(), { color: '#c080ff', to: 7, life: 0.5 });
      this.fx.particles.burst(p.clone().add(new THREE.Vector3(0, 1, 0)), { count: 24, colors: ['#c080ff', '#ffffff'], speed: 6, up: 2, glow: true, bright: 1.8, size: 0.1 });
      this.fx.worldUI.float('🧠 BRAIN BLAST!', p.clone().add(new THREE.Vector3(0, 2.6, 0)), 'ft-purple ft-big');
      audio.play('zap');
    }
  }

  playerDied(reason) {
    if (this.player.dead || this.state === 'win') return;
    this.player.dead = true;
    this.stats.falls++;
    const g = this.game;
    audio.play('whoosh');
    haptics.play('fall');
    g.ui.fade(true);
    setTimeout(() => {
      if (this.disposed) return;
      const cp = this.checkpoint;
      const p = cp.pos.clone();
      this.player.spawn(p, this.player.facing);
      if (reason === 'hearts') this.player.hearts = this.player.maxHearts;
      g.camRig.snap(this.player.pos, g.camRig.yaw);
      g.ui.fade(false);
      audio.play('respawn');
      this.fx.particles.burst(p, { count: 16, colors: ['#ffffff', '#bfe8ff'], speed: 3, up: 4, glow: true, size: 0.1 });
      if (reason === 'fall' && this.stats.falls === 3) g.ui.toast('Tip: hold jump for big jumps, tap it for small hops! 👟', 'blue');
    }, 650);
  }

  enterPortal() {
    if (this.state === 'win') return;
    if(this.expedition&&this.expedition.done.size<3)return;
    this.expedition?.award();
    this.state = 'win';
    const g = this.game;
    const pl = this.player;
    pl.frozen = true;
    pl.vel.set(0, 0, 0);
    pl.celebrate = true;
    audio.play('portal');
    haptics.play('levelComplete');
    g.input.showTouchControls(false);
    this.fx.worldUI.hide('prompt');
    this.winT = 0;
    this.winYaw = g.camRig.yaw;
    const P = this.fx.particles;
    for (let i = 0; i < 6; i++)
      setTimeout(() => {
        if (this.disposed) return;
        P.firework(pl.pos.clone().add(new THREE.Vector3((Math.random() - 0.5) * 10, 6 + Math.random() * 4, (Math.random() - 0.5) * 10)));
        audio.play('pop');
      }, 250 + i * 280);
    P.confetti(pl.pos.clone().add(new THREE.Vector3(0, 2, 0)), 90, 2);
    setTimeout(() => audio.play('levelComplete'), 300);
    setTimeout(() => {
      if (!this.disposed) g.levelComplete(this.results());
    }, 2600);
  }

  winCam(dt) {
    this.winT += dt;
    const c = this.game.camera;
    const p = this.player.pos;
    const a = this.winYaw + Math.PI + this.winT * 0.45;
    c.position.set(p.x + Math.sin(a) * 5.5, p.y + 2.2, p.z + Math.cos(a) * 5.5);
    c.lookAt(p.x, p.y + 1.2, p.z);
  }

  results() {
    const s = this.stats;
    const acc = s.asked ? s.firstTry / s.asked : 1;
    const stars = this.isBoss ? [true, acc >= 0.8, this.player.damageTaken <= 1] : [true, acc >= 0.8, s.secret];
    return {
      id: this.id,
      name: this.name,
      isBoss: this.isBoss,
      stars,
      coins: s.coins,
      gems: s.gems,
      xp: s.xp + 50 + (this.isBoss ? 150 : 0) + stars.filter(Boolean).length * 20,
      time: s.time || this.time,
      asked: s.asked,
      firstTry: s.firstTry,
      accuracy: acc,
      mobs: s.mobs,
      stomps: s.stomps,
      lucky: s.lucky,
      chests: s.chests,
      falls: s.falls,
      jumps: s.jumps,
      secret: s.secret,
      noDamage: this.player.damageTaken === 0,
      totalCoins: this.totalCoins,
    };
  }

  progress() {
    const [a, b] = this.progressRange;
    return Math.max(0, Math.min(1, (this.player.pos.z - a) / (b - a)));
  }

  dispose() {
    this.disposed = true;
    this.expedition?.dispose();
    for (const e of this.entities) e.dispose();
    for (const m of this.mobs) m.dispose();
    if (this.boss) this.boss.dispose();
    this.coins.dispose();
    this.character.dispose();
    this.world.dispose();
    this.game.scene.remove(this.group);
    this.fx.particles.clear();
    this.fx.rings.clear();
    this.fx.beams.clear();
    this.fx.worldUI.clear();
  }
}

const NO_INPUT = { moveX: 0, moveY: 0, jumpPressed: false, jumpHeld: false, actionPressed: false, camDX: 0, camDY: 0, wheel: 0, taps: [], touchMode: false };
